/**
 * Cart — Market Stop-Selling Guard
 *
 * Holds the cart's Checkout while a line carries `data-market-blocked`: a product this market's
 * stop-selling switch holds sold out (cart-item.liquid, product-market-sellable.liquid). Shopify
 * would still check that line out, so this is what keeps it out of an order. It is a light guard
 * by design: a cart permalink or a direct checkout link never passes through the theme.
 *
 * Both cart surfaces, the drawer and the cart page, re-render their lines on every change, so the
 * state is re-read whenever a form's `[data-products]` changes instead of being hooked into each
 * rebuild path.
 *
 * The drawer's lines arrive by fetch after load, so until the first render lands (`.cart__items`
 * inside `[data-products]`) there is no line to read. cart-drawer.liquid marks the form
 * `data-market-held` when the cart it rendered with holds a stopped line, and that mark holds
 * Checkout until the first render replaces it, so a slow or failed fetch fails closed.
 *
 * Checkout is marked `aria-disabled` rather than `disabled`: bfx-cart-remove.js disables the same
 * button around a removal and re-enables only the ones it disabled, so a second owner of
 * `disabled` would either be undone by it or undo it. The submit listener does the holding, on
 * the document in the capture phase so it runs before Impulse's own cart submit handler. Express
 * checkouts are Shopify's markup with no button to disable, so their container is hidden.
 */
const initCartMarketGuard = () => {
  const forms = [...document.querySelectorAll('#CartDrawerForm, #CartPageForm')];
  if (!forms.length) return null;

  const isBlocked = (form) =>
    Boolean(form.querySelector('[data-market-blocked]')) ||
    (form.hasAttribute('data-market-held') && !form.querySelector('[data-products] .cart__items'));

  const sync = (form) => {
    const blocked = isBlocked(form);

    form.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (blocked) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    });

    form.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      container.hidden = blocked;
    });

    // A live region that stays in the DOM and only changes its text, so the change is announced.
    const note = form.querySelector('[data-market-note]');
    if (note) {
      const message = blocked ? note.dataset.marketNote : '';
      if (note.textContent !== message) note.textContent = message;
    }
  };

  const abortController = new AbortController();
  const { signal } = abortController;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!forms.includes(form) || event.submitter?.name !== 'checkout' || !isBlocked(form)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true, signal }
  );

  const observers = forms.map((form) => {
    sync(form);
    const products = form.querySelector('[data-products]');
    if (!products) return null;
    const observer = new MutationObserver(() => sync(form));
    observer.observe(products, { childList: true, subtree: true });
    return observer;
  });

  return () => {
    abortController.abort();
    observers.forEach((observer) => observer?.disconnect());
  };
};

initCartMarketGuard();
